import { apiClient } from "@/lib/api";
import type {
  CancellationReason,
  CustomFieldSchema,
  DeliveryRequirement,
  TenantPlanningSettings,
  TenantPlanningSettingsUpdatePayload,
  TenantSettings,
  TenantSettingsUpdatePayload,
} from "@/types/settings";

// ---------------------------------------------------------------------------
// Tenant Settings
// ---------------------------------------------------------------------------

/**
 * Fetches the tenant's operational settings.
 * GET /api/v1/settings/
 */
export async function getSettings(): Promise<TenantSettings> {
  const response = await apiClient.get<TenantSettings>("/v1/settings/");
  return response.data;
}

/**
 * Partially updates the tenant's operational settings.
 * PATCH /api/v1/settings/
 *
 * @param payload - Only the fields that have changed (PATCH semantics).
 */
export async function updateSettings(
  payload: TenantSettingsUpdatePayload,
): Promise<TenantSettings> {
  const response = await apiClient.patch<TenantSettings>(
    "/v1/settings/",
    payload,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Planning Settings
// ---------------------------------------------------------------------------

/**
 * Fetches the tenant's planning / routing settings.
 * GET /api/v1/settings/planning/
 */
export async function getPlanningSettings(): Promise<TenantPlanningSettings> {
  const response = await apiClient.get<TenantPlanningSettings>(
    "/v1/settings/planning/",
  );
  return response.data;
}

/**
 * How many PLANNED routes an "apply depots" run would affect.
 * GET /api/v1/settings/planning/apply-depot-to-planned-routes/
 *
 * Used to state the number in the confirm dialog BEFORE anything is written — the action
 * rewrites warehouses and re-optimizes stop order, so it must never be a blind shot.
 */
export async function getApplyDepotPreview(): Promise<{ route_count: number }> {
  const response = await apiClient.get<{ route_count: number }>(
    "/v1/settings/planning/apply-depot-to-planned-routes/",
  );
  return response.data;
}

/**
 * Applies the tenant's default warehouses to every PLANNED route, then re-optimizes them.
 * POST /api/v1/settings/planning/apply-depot-to-planned-routes/
 *
 * BUG-542: this used to happen IMPLICITLY on every settings save — flipping the depot
 * switch, or merely picking a different default warehouse, silently rebuilt tomorrow's
 * finished plans. It is now an explicit action behind a confirm dialog, and saving the
 * settings form has no effect on existing routes at all.
 */
export async function applyDepotToPlannedRoutes(): Promise<{
  route_count: number;
}> {
  const response = await apiClient.post<{ route_count: number }>(
    "/v1/settings/planning/apply-depot-to-planned-routes/",
  );
  return response.data;
}

/**
 * Partially updates the tenant's planning / routing settings.
 * PATCH /api/v1/settings/planning/
 *
 * Use `default_start_warehouse_id`, `default_mid_warehouse_id`,
 * `default_end_warehouse_id` (number | null) instead of the nested warehouse objects.
 *
 * @param payload - Only the fields that have changed (PATCH semantics).
 */
export async function updatePlanningSettings(
  payload: TenantPlanningSettingsUpdatePayload,
): Promise<TenantPlanningSettings> {
  const response = await apiClient.patch<TenantPlanningSettings>(
    "/v1/settings/planning/",
    payload,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Cancellation Reasons
// ---------------------------------------------------------------------------

/**
 * Fetches the list of all cancellation reasons for the tenant.
 * GET /api/v1/settings/cancellation-reasons/
 */
export async function getCancellationReasons(): Promise<CancellationReason[]> {
  const response = await apiClient.get<CancellationReason[]>(
    "/v1/settings/cancellation-reasons/",
  );
  return response.data;
}

/**
 * Creates a new cancellation reason.
 * POST /api/v1/settings/cancellation-reasons/
 *
 * @param payload - Object containing the reason `name`.
 */
export async function createCancellationReason(payload: {
  name: string;
}): Promise<CancellationReason> {
  const response = await apiClient.post<CancellationReason>(
    "/v1/settings/cancellation-reasons/",
    payload,
  );
  return response.data;
}

/**
 * Updates an existing cancellation reason.
 * PATCH /api/v1/settings/cancellation-reasons/{id}/
 *
 * @param id      - ID of the reason to update.
 * @param payload - Object containing the new `name`.
 */
export async function updateCancellationReason(
  id: number,
  payload: { name: string },
): Promise<CancellationReason> {
  const response = await apiClient.patch<CancellationReason>(
    `/v1/settings/cancellation-reasons/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes a cancellation reason.
 * DELETE /api/v1/settings/cancellation-reasons/{id}/
 *
 * @param id - ID of the reason to delete.
 */
export async function deleteCancellationReason(id: number): Promise<void> {
  await apiClient.delete(`/v1/settings/cancellation-reasons/${id}/`);
}

/**
 * Saves the display order of cancellation reasons.
 * POST /api/v1/settings/cancellation-reasons/reorder/
 *
 * @param ids - Ordered array of reason IDs representing the desired display order.
 */
export async function reorderCancellationReasons(ids: number[]): Promise<void> {
  await apiClient.post("/v1/settings/cancellation-reasons/reorder/", { ids });
}

// ---------------------------------------------------------------------------
// Custom Fields
// ---------------------------------------------------------------------------

/**
 * Fetches the list of all custom order field schemas for the tenant.
 * GET /api/v1/settings/custom-fields/
 */
export async function getCustomFields(): Promise<CustomFieldSchema[]> {
  const response = await apiClient.get<CustomFieldSchema[]>(
    "/v1/settings/custom-fields/",
  );
  return response.data;
}

/**
 * Creates a new custom field schema.
 * POST /api/v1/settings/custom-fields/
 *
 * @param payload - Field `name` and optional `show_in_mobile` flag.
 */
export async function createCustomField(payload: {
  name: string;
  show_in_mobile?: boolean;
}): Promise<CustomFieldSchema> {
  const response = await apiClient.post<CustomFieldSchema>(
    "/v1/settings/custom-fields/",
    payload,
  );
  return response.data;
}

/**
 * Updates an existing custom field schema.
 * PATCH /api/v1/settings/custom-fields/{id}/
 *
 * @param id      - ID of the custom field to update.
 * @param payload - Optional `name` and/or `show_in_mobile` to change.
 */
export async function updateCustomField(
  id: number,
  payload: { name?: string; show_in_mobile?: boolean },
): Promise<CustomFieldSchema> {
  const response = await apiClient.patch<CustomFieldSchema>(
    `/v1/settings/custom-fields/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes a custom field schema.
 * DELETE /api/v1/settings/custom-fields/{id}/
 *
 * @param id - ID of the custom field to delete.
 */
export async function deleteCustomField(id: number): Promise<void> {
  await apiClient.delete(`/v1/settings/custom-fields/${id}/`);
}

/**
 * Saves the display order of custom fields.
 * POST /api/v1/settings/custom-fields/reorder/
 *
 * @param ids - Ordered array of custom field IDs representing the desired display order.
 */
export async function reorderCustomFields(ids: number[]): Promise<void> {
  await apiClient.post("/v1/settings/custom-fields/reorder/", { ids });
}

// ---------------------------------------------------------------------------
// Delivery Requirements
// ---------------------------------------------------------------------------

/**
 * Fetches the list of all delivery requirements for the tenant.
 * GET /api/v1/settings/delivery-requirements/
 */
export async function getDeliveryRequirements(): Promise<
  DeliveryRequirement[]
> {
  const response = await apiClient.get<DeliveryRequirement[]>(
    "/v1/settings/delivery-requirements/",
  );
  return response.data;
}

/**
 * Creates a new delivery requirement.
 * POST /api/v1/settings/delivery-requirements/
 *
 * @param payload - Object containing the requirement `name`.
 */
export async function createDeliveryRequirement(payload: {
  name: string;
}): Promise<DeliveryRequirement> {
  const response = await apiClient.post<DeliveryRequirement>(
    "/v1/settings/delivery-requirements/",
    payload,
  );
  return response.data;
}

/**
 * Deletes a delivery requirement.
 * DELETE /api/v1/settings/delivery-requirements/{id}/
 *
 * @param id - ID of the requirement to delete.
 */
export async function deleteDeliveryRequirement(id: number): Promise<void> {
  await apiClient.delete(`/v1/settings/delivery-requirements/${id}/`);
}
